import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input, Modal, Select } from "antd";
import Table from "../../../components/Table/Table";
import Pagination from "../../../components/Pagination/Pagination";
import { Header, PaginationContainer, UnreadDot } from "../HomeStyled";
import { renderText } from "../../../utils/columns";
import {
  createInboxMessageThread,
  listInboxMessageRecipients,
} from "../../../service/inbox";

const person = (user) =>
  user?.name || user?.full_name || user?.username || user?.email ||
  "Unknown teammate";

const personList = (users) => {
  const names = (users || []).map(person).filter(Boolean);
  return names.length ? names.join(", ") : "Unknown teammate";
};

/**
 * Direct-message inbox. Thread rows deliberately mirror Shared for Review:
 * a compact pageable table whose row opens the related document/exchange.
 */
const MessagesTab = ({
  messagesList,
  currentPage = 1,
  handlePageChange,
  handleChangePageSize,
  filterState,
  onOpenThread,
}) => {
  const { messages = {} } = filterState;
  const [composeOpen, setComposeOpen] = useState(false);
  const [matterId, setMatterId] = useState("");
  const [recipientId, setRecipientId] = useState(null);
  const [ccIds, setCcIds] = useState([]);
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [composeError, setComposeError] = useState(null);

  useEffect(() => {
    if (!composeOpen) return;
    listInboxMessageRecipients()
      .then((response) => setRecipients(response.data || []))
      .catch(() => setComposeError("Couldn't load teammates."));
  }, [composeOpen]);

  const recipientOptions = useMemo(
    () =>
      recipients.map((user) => ({
        value: user.id,
        label: person(user),
      })),
    [recipients]
  );

  // Cc'd people become full members of the conversation — they see every
  // future reply and can reply themselves, same as the primary recipient.
  const ccOptions = useMemo(
    () => recipientOptions.filter((option) => option.value !== recipientId),
    [recipientOptions, recipientId]
  );

  useEffect(() => {
    setCcIds((current) => current.filter((id) => id !== recipientId));
  }, [recipientId]);

  const closeCompose = useCallback(() => {
    if (submitting) return;
    setComposeOpen(false);
    setMatterId("");
    setRecipientId(null);
    setCcIds([]);
    setBody("");
    setComposeError(null);
  }, [submitting]);

  const sendMatterMessage = useCallback(async () => {
    if (!matterId.trim() || recipientId == null || !body.trim()) return;
    setSubmitting(true);
    setComposeError(null);
    try {
      const response = await createInboxMessageThread({
        matter_id: matterId.trim(),
        recipient_user_id: recipientId,
        cc_user_ids: ccIds,
        body,
      });
      closeCompose();
      onOpenThread(response.data);
    } catch (error) {
      setComposeError(error?.message || "Couldn't send message.");
    } finally {
      setSubmitting(false);
    }
  }, [body, ccIds, closeCompose, matterId, onOpenThread, recipientId]);
  const columns = [
    {
      title: <Header>CONVERSATION</Header>,
      dataIndex: "latest_message",
      key: "latest_message",
      render: (_, record) => (
        <>
          {record?.unread_count > 0 && <UnreadDot aria-label="Unread" />}
          {record?.latest_message?.body || "Message thread"}
        </>
      ),
    },
    {
      title: <Header>WITH</Header>,
      dataIndex: "participants",
      key: "participants",
      render: personList,
    },
    {
      title: <Header>MATTER</Header>,
      dataIndex: "matter_name",
      key: "matter_name",
      render: (value, record) => value || record?.matter_id || "",
    },
    {
      title: <Header>DOCUMENT</Header>,
      dataIndex: "document_name",
      key: "document_name",
      render: (value) => value || "Matter message",
    },
    {
      title: <Header>LAST ACTIVITY</Header>,
      dataIndex: "last_message_at",
      key: "last_message_at",
      render: renderText,
    },
    {
      title: <Header>UNREAD</Header>,
      dataIndex: "unread_count",
      key: "unread_count",
      render: (value) => value || "",
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button type="primary" onClick={() => setComposeOpen(true)}>
          New matter message
        </Button>
      </div>
      <Table
        columns={columns}
        data={messagesList?.results || []}
        onRow={(record) => ({
          onClick: () => onOpenThread(record),
        })}
        rowClassName={(record) => (record?.unread_count > 0 ? "row-unread" : "")}
        emptyMessage="No messages found"
      />
      {messagesList?.results?.length > 0 && (
        <PaginationContainer>
          <Pagination
            total={messagesList.count}
            current={currentPage}
            pageSize={messages.page_size}
            onChange={(page) => handlePageChange({ label: "page", value: page })}
            adjustable={false}
            onPageSizeChange={handleChangePageSize}
          />
        </PaginationContainer>
      )}
      <Modal
        open={composeOpen}
        title="New matter message"
        onCancel={closeCompose}
        onOk={sendMatterMessage}
        okText="Send"
        okButtonProps={{
          loading: submitting,
          disabled: !matterId.trim() || recipientId == null || !body.trim(),
        }}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label htmlFor="home-message-matter-id">Matter ID</label>
            <Input
              id="home-message-matter-id"
              value={matterId}
              onChange={(event) => setMatterId(event.target.value)}
              placeholder="Salesforce Matter ID"
            />
          </div>
          <div>
            <label htmlFor="home-message-recipient">To</label>
            <Select
              id="home-message-recipient"
              style={{ width: "100%" }}
              value={recipientId}
              onChange={setRecipientId}
              options={recipientOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Choose a teammate"
            />
          </div>
          <div>
            <label htmlFor="home-message-cc">Cc</label>
            <Select
              id="home-message-cc"
              mode="multiple"
              style={{ width: "100%" }}
              value={ccIds}
              onChange={setCcIds}
              options={ccOptions}
              showSearch
              optionFilterProp="label"
              placeholder="Loop in other teammates (optional)"
            />
          </div>
          <div>
            <label htmlFor="home-message-body">Message</label>
            <Input.TextArea
              id="home-message-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              autoSize={{ minRows: 3, maxRows: 8 }}
              maxLength={10000}
            />
          </div>
          {composeError && <span role="alert">{composeError}</span>}
        </div>
      </Modal>
    </>
  );
};

MessagesTab.propTypes = {
  messagesList: PropTypes.shape({
    results: PropTypes.array,
    count: PropTypes.number,
  }),
  currentPage: PropTypes.number,
  handlePageChange: PropTypes.func.isRequired,
  handleChangePageSize: PropTypes.func,
  filterState: PropTypes.object.isRequired,
  onOpenThread: PropTypes.func.isRequired,
};

export default MessagesTab;
